import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { getCompanySettings, getPdfSettings } from './settings';

// Required for jspdf-autotable v5+ to enable doc.autoTable()
applyPlugin(jsPDF);

/**
 * Create PDF header with dynamic company info from settings
 * Priority: Company settings (company_name, print_header) > PDF config defaults
 */
const addPDFHeader = (doc, title, companyInfo, pdfConfig) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  const showHeader = pdfConfig?.show_header !== false;
  
  // Use company settings first, then fall back to PDF config defaults
  // Company print_header or company_name takes priority over PDF config header_text
  const headerText = companyInfo.print_header || companyInfo.company_name || pdfConfig?.header_text || 'Jai Matadi Grey Order Management';
  const address = companyInfo.address || '';
  
  if (showHeader) {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(headerText, pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(address, pageWidth / 2, 28, { align: 'center' });
    
    if (companyInfo.phone || companyInfo.email) {
      const contactLine = [companyInfo.phone, companyInfo.email].filter(Boolean).join(' | ');
      doc.text(contactLine, pageWidth / 2, 34, { align: 'center' });
    }
  }
  
  doc.setLineWidth(0.5);
  const lineY = showHeader ? 38 : 15;
  doc.line(15, lineY, pageWidth - 15, lineY);
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, lineY + 10, { align: 'center' });
  
  return lineY + 20;
};

/**
 * Add footer with dynamic text from settings
 * Priority: Company settings (print_footer) > PDF config defaults
 */
const addPDFFooter = (doc, companyInfo, pdfConfig) => {
  const showFooter = pdfConfig?.show_footer !== false;
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Use company print_footer first, then PDF config footer_text
  const footerText = companyInfo.print_footer || pdfConfig?.footer_text || '';
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 15, pageHeight - 10);
  
  if (showFooter && footerText) {
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }
};

/**
 * Add key-value field to PDF
 */
const addField = (doc, label, value, x, y, labelWidth = 45) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(`${label}:`, x, y);
  
  doc.setFont('helvetica', 'normal');
  doc.text(String(value || '-'), x + labelWidth, y);
  
  return y + 7;
};

/**
 * Export Grey Order to PDF
 */
export const exportOrderToPDF = async (order) => {
  const [companyInfo, pdfConfig] = await Promise.all([
    getCompanySettings(),
    getPdfSettings('grey_orders')
  ]);
  
  const doc = new jsPDF();
  let y = addPDFHeader(doc, 'Grey Order Details', companyInfo, pdfConfig);
  
  y = addField(doc, 'Order Number', order.order_number, 15, y);
  y = addField(doc, 'Order Date', order.order_date, 15, y);
  y = addField(doc, 'Status', order.order_status, 15, y);
  
  y += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Supplier Details', 15, y);
  y += 7;
  y = addField(doc, 'Supplier', order.supplier_name, 15, y);
  y = addField(doc, 'City', order.city, 15, y);
  
  y += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Product Details', 15, y);
  y += 7;
  y = addField(doc, 'Item', order.item || order.item_name || '-', 15, y);
  y = addField(doc, 'Quality', order.quality_name, 15, y);
  y = addField(doc, 'Finish Quality', order.subquality_name || '-', 15, y);
  
  y += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Quantity & Value', 15, y);
  y += 7;
  y = addField(doc, 'Ordered Qty', `${order.ordered_quantity} ${order.order_unit}`, 15, y);
  y = addField(doc, 'Rate', `₹${order.rate} / ${order.order_unit}`, 15, y);
  y = addField(doc, 'Order Value', `₹${order.order_amount?.toLocaleString() || '-'}`, 15, y);
  y = addField(doc, 'Billed Qty', `${order.total_billed_quantity || 0} ${order.order_unit}`, 15, y);
  y = addField(doc, 'Received Qty', `${order.total_received_quantity || 0} ${order.order_unit}`, 15, y);
  y = addField(doc, 'Pending Quantity', `${order.pending_quantity || 0} ${order.order_unit}`, 15, y);
  
  if (order.remarks) {
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Remarks', 15, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(order.remarks, 15, y, { maxWidth: 180 });
  }
  
  addPDFFooter(doc, companyInfo, pdfConfig);
  doc.save(`Order_${order.order_number}.pdf`);
};

/**
 * Export Grey Bill to PDF
 */
export const exportBillToPDF = async (bill, order) => {
  const [companyInfo, pdfConfig] = await Promise.all([
    getCompanySettings(),
    getPdfSettings('grey_bills')
  ]);
  
  const doc = new jsPDF();
  let y = addPDFHeader(doc, 'Grey Bill Details', companyInfo, pdfConfig);
  
  y = addField(doc, 'Bill Number', bill.bill_number || bill.purchase_bill_number, 15, y);
  y = addField(doc, 'Bill Date', bill.billed_date, 15, y);
  y = addField(doc, 'Related Order', order?.order_number || bill.order_number || '-', 15, y);
  
  y += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Party Details', 15, y);
  y += 7;
  y = addField(doc, 'Party Name', bill.party_name, 15, y);
  
  y += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Billing Details', 15, y);
  y += 7;
  y = addField(doc, 'Quantity', bill.billed_quantity, 15, y);
  y = addField(doc, 'Rate', `₹${bill.bill_rate}`, 15, y);
  y = addField(doc, 'Amount', `₹${bill.amount?.toLocaleString() || '-'}`, 15, y);
  y = addField(doc, 'Freight', bill.freight_charges ? `₹${bill.freight_charges}` : '-', 15, y);
  
  addPDFFooter(doc, companyInfo, pdfConfig);
  doc.save(`Bill_${bill.bill_number || bill.purchase_bill_number}.pdf`);
};

/**
 * Export LR Report to PDF
 */
export const exportLRToPDF = async (lr, order) => {
  const [companyInfo, pdfConfig] = await Promise.all([
    getCompanySettings(),
    getPdfSettings('lr_reports')
  ]);
  
  const doc = new jsPDF();
  let y = addPDFHeader(doc, 'LR Report Details', companyInfo, pdfConfig);
  
  y = addField(doc, 'LR Number', lr.lr_number, 15, y);
  y = addField(doc, 'LR Date', lr.lr_date, 15, y);
  y = addField(doc, 'Status', lr.status, 15, y);
  y = addField(doc, 'Related Order', order?.order_number || lr.order_number || '-', 15, y);
  
  y += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Transport Details', 15, y);
  y += 7;
  y = addField(doc, 'Transport', lr.transport_name, 15, y);
  y = addField(doc, 'Bilty Number', lr.bilty_number || '-', 15, y);
  y = addField(doc, 'Bilty Date', lr.bilty_date || '-', 15, y);
  y = addField(doc, 'Bales', lr.bales_in_lr || '-', 15, y);
  
  if (lr.remarks) {
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Remarks', 15, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(lr.remarks, 15, y, { maxWidth: 180 });
  }
  
  addPDFFooter(doc, companyInfo, pdfConfig);
  doc.save(`LR_${lr.lr_number}.pdf`);
};

/**
 * Export Grey Receipt to PDF
 */
export const exportReceiptToPDF = async (receipt, order) => {
  const [companyInfo, pdfConfig] = await Promise.all([
    getCompanySettings(),
    getPdfSettings('grey_receipts')
  ]);
  
  const doc = new jsPDF();
  let y = addPDFHeader(doc, 'Grey Receipt Details', companyInfo, pdfConfig);
  
  y = addField(doc, 'Receipt Number', receipt.receipt_number, 15, y);
  y = addField(doc, 'Received Date', receipt.received_date, 15, y);
  y = addField(doc, 'Related Order', order?.order_number || receipt.order_number || '-', 15, y);
  
  y += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Receipt Details', 15, y);
  y += 7;
  y = addField(doc, 'Location', receipt.location_name || '-', 15, y);
  y = addField(doc, 'Quantity', receipt.received_quantity, 15, y);
  y = addField(doc, 'Gate Status', receipt.gate_status, 15, y);
  y = addField(doc, 'Verified By', receipt.verified_by, 15, y);
  
  if (receipt.discrepancy_notes) {
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Discrepancy Notes', 15, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(receipt.discrepancy_notes, 15, y, { maxWidth: 180 });
  }
  
  addPDFFooter(doc, companyInfo, pdfConfig);
  doc.save(`Receipt_${receipt.receipt_number}.pdf`);
};

/**
 * Export LR Delivery Issue to PDF
 */
/**
 * Export Dashboard to PDF (Overview or Item-wise)
 */
export const exportDashboardToPDF = async (tabType, data, filters, filterOptions) => {
  const [companyInfo, pdfConfig] = await Promise.all([
    getCompanySettings(),
    getPdfSettings('grey_orders')
  ]);

  const doc = new jsPDF();
  const title = tabType === 'overview' ? 'Overview Dashboard Report' : 'Item-wise Dashboard Report';
  let y = addPDFHeader(doc, title, companyInfo, pdfConfig);

  // Active filters
  const activeFilters = [];
  if (filters.date_from) activeFilters.push(`From: ${filters.date_from}`);
  if (filters.date_to) activeFilters.push(`To: ${filters.date_to}`);
  if (filters.supplier_id) {
    const s = filterOptions.suppliers?.find(s => s.supplier_id === filters.supplier_id);
    if (s) activeFilters.push(`Supplier: ${s.name}`);
  }
  if (filters.quality_id) {
    const q = filterOptions.qualities?.find(q => q.quality_id === filters.quality_id);
    if (q) activeFilters.push(`Quality: ${q.quality_name}`);
  }
  if (filters.item_id) {
    const i = filterOptions.items?.find(i => i.item_id === filters.item_id);
    if (i) activeFilters.push(`Item: ${i.item_name}`);
  }
  if (filters.status) activeFilters.push(`Status: ${filters.status}`);

  if (activeFilters.length > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(`Filters: ${activeFilters.join(' | ')}`, 15, y);
    y += 8;
  }

  if (tabType === 'overview') {
    const kpis = data.kpis || {};
    // KPI Summary
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Order Overview', 15, y);
    y += 7;

    doc.autoTable({
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Orders', String(kpis.total_orders || 0)],
        ['Pending Orders', String(kpis.pending_orders || 0)],
        ['Partially Billed', String(kpis.partially_billed_orders || 0)],
        ['Completed', String(kpis.completed_orders || 0)]
      ],
      theme: 'grid',
      headStyles: { fillColor: [15, 23, 42] },
      margin: { left: 15, right: 15 },
      styles: { fontSize: 9 }
    });
    y = doc.lastAutoTable.finalY + 10;

    // LR Status
    if (kpis.lr_status) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('LR Status', 15, y);
      y += 7;
      doc.autoTable({
        startY: y,
        head: [['Status', 'Count']],
        body: [
          ['In Transport', String(kpis.lr_status.in_transport || 0)],
          ['Issued for Delivery', String(kpis.lr_status.issued_for_delivery || 0)],
          ['Delivered', String(kpis.lr_status.delivered || 0)]
        ],
        theme: 'grid',
        headStyles: { fillColor: [15, 23, 42] },
        margin: { left: 15, right: 15 },
        styles: { fontSize: 9 }
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // Quantity Summary
    if (kpis.unit_wise_quantities && Object.keys(kpis.unit_wise_quantities).length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Quantity Summary', 15, y);
      y += 7;
      const qtyRows = [];
      Object.entries(kpis.unit_wise_quantities).forEach(([unit, q]) => {
        qtyRows.push([unit, String(q.ordered), String(q.billed), String(q.received), String(q.pending)]);
      });
      doc.autoTable({
        startY: y,
        head: [['Unit', 'Ordered', 'Billed', 'Received', 'Pending Quantity']],
        body: qtyRows,
        theme: 'grid',
        headStyles: { fillColor: [15, 23, 42] },
        margin: { left: 15, right: 15 },
        styles: { fontSize: 9 }
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // Item Analytics
    if (data.item_analytics?.length > 0) {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Item-wise Analytics', 15, y);
      y += 7;
      doc.autoTable({
        startY: y,
        head: [['Item', 'Orders', 'Ordered', 'Billed', 'Received', 'Pending Quantity']],
        body: data.item_analytics.map(i => [
          i.item_name, String(i.order_count), String(i.total_ordered),
          String(i.total_billed), String(i.total_received), String(i.pending)
        ]),
        theme: 'grid',
        headStyles: { fillColor: [15, 23, 42] },
        margin: { left: 15, right: 15 },
        styles: { fontSize: 8 }
      });
    }
  } else {
    // Item-wise dashboard
    const summary = data.summary || {};
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary KPIs', 15, y);
    y += 7;
    doc.autoTable({
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Ordered Qty', String(summary.total_ordered_qty || 0)],
        ['In Transport (LRs)', String(summary.total_in_transport || 0)],
        ['Issued for Delivery', String(summary.total_issued || 0)],
        ['Total Received', String(summary.total_received || 0)],
        ['Total Pending Quantity', String(summary.total_pending || 0)]
      ],
      theme: 'grid',
      headStyles: { fillColor: [15, 23, 42] },
      margin: { left: 15, right: 15 },
      styles: { fontSize: 9 }
    });
    y = doc.lastAutoTable.finalY + 10;

    // Per-item breakdown
    const items = data.items || [];
    for (const item of items) {
      if (y > 220) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(item.item_name, 15, y);
      y += 6;

      // Order overview
      doc.autoTable({
        startY: y,
        head: [['Total', 'Pending', 'Partially Billed', 'Completed']],
        body: [[
          String(item.order_overview.total),
          String(item.order_overview.pending),
          String(item.order_overview.partially_billed),
          String(item.order_overview.completed)
        ]],
        theme: 'grid',
        headStyles: { fillColor: [51, 65, 85] },
        margin: { left: 15, right: 15 },
        styles: { fontSize: 8 }
      });
      y = doc.lastAutoTable.finalY + 5;

      // Quantity summary
      const qtyRows = [];
      Object.entries(item.quantity_summary || {}).forEach(([unit, q]) => {
        qtyRows.push([unit, String(q.ordered), String(q.billed), String(q.received), String(q.pending)]);
      });
      if (qtyRows.length > 0) {
        doc.autoTable({
          startY: y,
          head: [['Unit', 'Ordered', 'Billed', 'Received', 'Pending Quantity']],
          body: qtyRows,
          theme: 'grid',
          headStyles: { fillColor: [51, 65, 85] },
          margin: { left: 15, right: 15 },
          styles: { fontSize: 8 }
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  addPDFFooter(doc, companyInfo, pdfConfig);
  doc.save(`Dashboard_${tabType}_${new Date().toISOString().slice(0, 10)}.pdf`);
};

export const exportLRIssueToPDF = async (issue, lrs = []) => {
  const [companyInfo, pdfConfig] = await Promise.all([
    getCompanySettings(),
    getPdfSettings('lr_issues')
  ]);
  
  const doc = new jsPDF();
  let y = addPDFHeader(doc, 'LR Delivery Issue', companyInfo, pdfConfig);
  
  y = addField(doc, 'Issue ID', issue.lr_delivery_issue_id, 15, y);
  y = addField(doc, 'Issue Date', issue.issue_date, 15, y);
  
  y += 5;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Driver & Vehicle Details', 15, y);
  y += 7;
  y = addField(doc, 'Driver Name', issue.driver_name, 15, y);
  y = addField(doc, 'Mobile', issue.driver_mobile, 15, y);
  y = addField(doc, 'Vehicle No', issue.vehicle_number, 15, y);
  y = addField(doc, 'Gate Pass', issue.gate_pass_no, 15, y);
  y = addField(doc, 'Issued By', issue.issued_by, 15, y);
  
  if (lrs.length > 0) {
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.text('LRs Issued', 15, y);
    y += 5;
    
    doc.autoTable({
      startY: y,
      head: [['LR Number', 'Order', 'Transport', 'Bales']],
      body: lrs.map(lr => [
        lr.lr_number,
        lr.order_number || '-',
        lr.transport_name || '-',
        lr.bales_in_lr || '-'
      ]),
      theme: 'grid',
      headStyles: { fillColor: [15, 23, 42] },
      margin: { left: 15, right: 15 }
    });
  }
  
  if (issue.remarks) {
    const finalY = doc.lastAutoTable?.finalY || y + 20;
    doc.setFont('helvetica', 'bold');
    doc.text('Remarks', 15, finalY + 10);
    doc.setFont('helvetica', 'normal');
    doc.text(issue.remarks, 15, finalY + 17, { maxWidth: 180 });
  }
  
  addPDFFooter(doc, companyInfo, pdfConfig);
  doc.save(`LR_Issue_${issue.lr_delivery_issue_id}.pdf`);
};
